import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '@/components/base/LanguageSwitcher';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const { t } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [catOpen, setCatOpen] = useState(true);
  const mobileRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (mobileRef.current && !mobileRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        scrolled || menuOpen
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/60'
          : 'bg-transparent'
      }`}
    >
      <nav className="w-full px-5 md:px-10">
        <div className="flex h-16 items-center justify-between">
          <Link
            to="/"
            onClick={() => setMenuOpen(false)}
            className={`font-heading text-lg md:text-xl font-semibold tracking-tight whitespace-nowrap transition-colors duration-300 ${
              scrolled || menuOpen ? 'text-foreground-900' : 'text-white'
            }`}
          >
            {t('brand')}
          </Link>

          <div className="hidden md:flex items-center gap-7">
            {[
              { key: 'nav.home', href: '/', internal: true },
              { key: 'nav.categories', href: '#categories' },
              { key: 'nav.guides', href: '#articles' },
              { key: 'nav.about', href: '#about' },
              { key: 'nav.faq', href: '#faq' },
            ].map((link) =>
              link.internal ? (
                <Link
                  key={link.key}
                  to="/"
                  className={`text-sm font-medium transition-colors duration-200 whitespace-nowrap ${
                    scrolled
                      ? 'text-foreground-700 hover:text-foreground-900'
                      : 'text-white/80 hover:text-white'
                  }`}
                >
                  {t(link.key)}
                </Link>
              ) : (
                <a
                  key={link.key}
                  href={link.href}
                  className={`text-sm font-medium transition-colors duration-200 whitespace-nowrap ${
                    scrolled
                      ? 'text-foreground-700 hover:text-foreground-900'
                      : 'text-white/80 hover:text-white'
                  }`}
                >
                  {t(link.key)}
                </a>
              )
            )}
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-1.5 px-5 py-2 rounded-full text-sm font-semibold whitespace-nowrap cursor-pointer bg-primary-500 text-white hover:bg-primary-600 transition-all duration-200"
            >
              {t('nav.sheet')}
              <i className="ri-arrow-right-up-line text-sm"></i>
            </a>
          </div>

          <div className="flex items-center gap-2">
            <LanguageSwitcher />
            <button
              type="button"
              onClick={() => setMenuOpen((v) => !v)}
              className={`md:hidden w-10 h-10 flex items-center justify-center rounded-lg cursor-pointer transition-all duration-200 ${
                scrolled || menuOpen
                  ? 'bg-background-100 text-foreground-900 hover:bg-background-200'
                  : 'bg-white/10 text-white hover:bg-white/20'
              }`}
              aria-label="Toggle menu"
            >
              <i className={`text-xl ${menuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </button>
          </div>
        </div>
      </nav>

      {menuOpen && (
        <div
          ref={mobileRef}
          className="md:hidden border-t border-background-200 bg-background-50 px-5 py-4 shadow-xl max-h-[85vh] overflow-y-auto"
        >
          {/* Primary nav links */}
          <div className="space-y-1">
            {[
              { key: 'nav.home', href: '/', internal: true },
              { key: 'nav.categories', href: '#categories' },
              { key: 'nav.guides', href: '#articles' },
              { key: 'nav.about', href: '#about' },
              { key: 'nav.faq', href: '#faq' },
            ].map((link) =>
              link.internal ? (
                <Link
                  key={link.key}
                  to="/"
                  onClick={() => setMenuOpen(false)}
                  className="flex items-center gap-3 px-3 py-2.5 text-sm font-medium text-foreground-800 hover:bg-background-100 rounded-lg transition"
                >
                  <i className="ri-home-4-line text-foreground-400"></i>
                  {t(link.key)}
                </Link>
              ) : (
                <a
                  key={link.key}
                  href={link.href}
                  onClick={() => setMenuOpen(false)}
                  className="flex items-center gap-3 px-3 py-2.5 text-sm font-medium text-foreground-800 hover:bg-background-100 rounded-lg transition"
                >
                  <i className="ri-compass-3-line text-foreground-400"></i>
                  {t(link.key)}
                </a>
              )
            )}
          </div>

          {/* Categories section — expanded by default on mobile */}
          <div className="mt-3 pt-3 border-t border-background-200">
            <div className="flex items-center justify-between px-3 mb-2">
              <span className="text-xs font-semibold uppercase tracking-wider text-foreground-500">
                {t('nav.categories')}
              </span>
              <button
                type="button"
                onClick={() => setCatOpen((v) => !v)}
                className="text-xs text-primary-600 font-medium flex items-center gap-1"
              >
                {catOpen ? t('nav.collapse') : t('nav.expand')}
                <i className={`ri-arrow-down-s-line transition-transform ${catOpen ? 'rotate-180' : ''}`}></i>
              </button>
            </div>
            {catOpen && (
              <div className="grid grid-cols-2 gap-1.5">
                {categories.map((cat) => (
                  <a
                    key={cat.id}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-2 px-3 py-2.5 text-sm text-foreground-700 hover:bg-background-100 rounded-lg transition border border-background-200/50"
                    onClick={() => setMenuOpen(false)}
                  >
                    <span className="w-6 h-6 rounded-md bg-background-100 flex items-center justify-center text-xs text-foreground-500">
                      <i className="ri-price-tag-3-line"></i>
                    </span>
                    <span className="flex-1 truncate">{t(`categories.${cat.id}`)}</span>
                    <i className="ri-external-link-line text-xs text-foreground-300"></i>
                  </a>
                ))}
              </div>
            )}
          </div>

          {/* CTA buttons */}
          <div className="mt-4 pt-4 border-t border-background-200 grid grid-cols-2 gap-3">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              onClick={() => setMenuOpen(false)}
              className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-primary-500 text-white text-sm font-semibold whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-all duration-200"
            >
              {t('nav.sheet')}
              <i className="ri-arrow-right-up-line text-sm"></i>
            </a>
            <Link
              to="/allchinabuy-spreadsheet-guide"
              onClick={() => setMenuOpen(false)}
              className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border border-background-300 text-foreground-800 text-sm font-semibold whitespace-nowrap cursor-pointer hover:bg-background-100 transition-all duration-200"
            >
              {t('nav.guide')}
              <i className="ri-book-open-line text-sm"></i>
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}